import { useState, type ReactNode } from "react";
import { ButtonV2 } from "./ui/button-v2.tsx";
import { Icon } from "./ui/icon.tsx";

export interface LineCommentData {
  id: string;
  filePath: string;
  lineNumber: number;
  author: string;
  text: string;
  createdAt: number;
}

export interface LineCommentProps {
  comment: LineCommentData;
  onDelete?: (id: string) => void;
}

export function LineComment({ comment, onDelete }: LineCommentProps) {
  return (
    <div
      data-component="line-comment"
      data-line={comment.lineNumber}
      className="my-1 rounded border border-kumo-line bg-kumo-surface p-2 text-xs text-kumo-default shadow-sm"
    >
      <div className="flex items-center justify-between text-[11px] text-kumo-subtle">
        <span className="font-semibold text-kumo-default">{comment.author}</span>
        <div className="flex items-center gap-2">
          <span>Line {comment.lineNumber}</span>
          {onDelete ? (
            <button
              type="button"
              className="text-kumo-subtle hover:text-rose-500"
              onClick={() => onDelete(comment.id)}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-1 whitespace-pre-wrap">{comment.text}</div>
    </div>
  );
}

export function LineCommentThread({
  filePath,
  lineNumber,
  comments,
  onAddComment,
  onDeleteComment
}: {
  filePath: string;
  lineNumber: number;
  comments: LineCommentData[];
  onAddComment?: (filePath: string, lineNumber: number, text: string) => void;
  onDeleteComment?: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const lineComments = comments.filter(
    (c) => c.filePath === filePath && c.lineNumber === lineNumber
  );

  const handleSubmit = () => {
    if (!draft.trim() || !onAddComment) return;
    onAddComment(filePath, lineNumber, draft.trim());
    setDraft("");
    setAdding(false);
  };

  return (
    <div data-component="line-comment-thread" data-line={lineNumber} className="my-1 pl-4">
      {lineComments.map((c) => (
        <LineComment key={c.id} comment={c} onDelete={onDeleteComment} />
      ))}
      {adding ? (
        <div className="mt-1 flex flex-col gap-1 rounded border border-kumo-line p-2 bg-kumo-surface">
          <textarea
            aria-label="Comment text"
            className="h-14 w-full resize-none rounded border border-kumo-line bg-transparent p-1.5 text-xs text-kumo-default outline-none"
            placeholder="Add line comment…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex justify-end gap-1">
            <ButtonV2 size="small" variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </ButtonV2>
            <ButtonV2 size="small" variant="neutral" onClick={handleSubmit}>
              Save Comment
            </ButtonV2>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1 text-[11px] text-kumo-subtle hover:text-kumo-default"
          onClick={() => setAdding(true)}
        >
          <Icon name="plus" size="small" />
          <span>Add comment on L{lineNumber}</span>
        </button>
      )}
    </div>
  );
}
